import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { staticDataService } from "../../utils/services/static-data.service";
import { useMetricsUi, MONO_FONT } from "../../components/metrics/metricsUi";

const MILESTONES = [
  "Gathering demand history",
  "Building the forecast request",
  "Running the model",
  "Computing reorder metrics",
  "Assembling results",
];

const fmtClock = (s) => {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
};

/**
 * "Running" view for a Planning > Forecast run — a segmented progress bar
 * (the timeline) plus a callout for the current milestone. Styled with the
 * page's design tokens.
 */
export default function ForecastRunningSteps() {
  const ui = useMetricsUi();
  const { colors } = ui;

  const [expectedTime, setExpectedTime] = useState(120);
  const [step, setStep] = useState(0);
  const [pct, setPct] = useState(0); // progress within the current step
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    let cancelled = false;
    staticDataService
      .forecastExecutionTime()
      .then((res) => {
        const v = Number(res?.data?.[0]?.value);
        if (!cancelled && Number.isFinite(v) && v > 0) setExpectedTime(v);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    setStep(0);
    setPct(0);
    const total = MILESTONES.length;
    const perStepMs = Math.max(
      1000,
      (Number(expectedTime || 120) * 1000) / total,
    );
    const tick = 250;
    const id = setInterval(() => {
      setPct((p) => {
        const next = p + (100 * tick) / perStepMs;
        if (next >= 100) {
          setStep((s) => (s >= total - 1 ? s : s + 1));
          return 0;
        }
        return next;
      });
    }, tick);
    return () => clearInterval(id);
  }, [expectedTime]);

  const total = MILESTONES.length;
  const overallPct = Math.min(
    99,
    Math.round(((step + pct / 100) / total) * 100),
  );
  const expectedMin = useMemo(
    () => Math.max(1, Math.round(Number(expectedTime) / 60)),
    [expectedTime],
  );

  return (
    <Box
      sx={{
        ...ui.card,
        p: { xs: "20px", sm: "22px 24px" },
        "@keyframes pf-pulse": {
          "0%,100%": { opacity: 1 },
          "50%": { opacity: 0.4 },
        },
        "@keyframes pf-slide": {
          "0%": { transform: "translateX(-100%)" },
          "100%": { transform: "translateX(200%)" },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box
            sx={{
              width: 9,
              height: 9,
              borderRadius: "50%",
              bgcolor: ui.accent,
              animation: "pf-pulse 1.3s ease-in-out infinite",
            }}
          />
          <Typography
            sx={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary }}
          >
            Generating forecast
          </Typography>
        </Box>
        <Typography
          sx={{
            fontFamily: MONO_FONT,
            fontSize: 13,
            color: colors.textSecondary,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {fmtClock(elapsed)}
        </Typography>
      </Box>

      {/* Segmented progress bar — the timeline */}
      <Box sx={{ display: "flex", gap: 0.75, mt: 2.25 }}>
        {MILESTONES.map((m, i) => {
          const done = i < step;
          const active = i === step;
          return (
            <Box
              key={m}
              sx={{
                flex: 1,
                height: 6,
                borderRadius: 3,
                position: "relative",
                overflow: "hidden",
                bgcolor: done ? ui.accent : ui.surfaceAlt,
              }}
            >
              {active && (
                <>
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      bgcolor: ui.accent,
                      width: `${Math.min(100, Math.max(8, pct))}%`,
                      transition: "width .25s linear",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      bottom: 0,
                      width: "35%",
                      background: `linear-gradient(90deg, transparent, ${colors.bgPaper}88, transparent)`,
                      animation: "pf-slide 1s linear infinite",
                    }}
                  />
                </>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Current milestone callout */}
      <Box sx={{ mt: 2.5 }}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: colors.textSecondary,
          }}
        >
          Step {Math.min(step + 1, total)} of {total}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: 1.5,
            mt: 0.5,
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{
              fontFamily: MONO_FONT,
              fontSize: 20,
              fontWeight: 650,
              color: colors.textPrimary,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {overallPct}%
          </Typography>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 650,
              color: ui.accent,
              lineHeight: 1.25,
            }}
          >
            {MILESTONES[Math.min(step, total - 1)]}
          </Typography>
        </Box>
      </Box>

      {/* Upcoming milestones */}
      <Box
        sx={{
          mt: 2.5,
          pt: 2,
          borderTop: `1px solid ${colors.border}`,
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        {MILESTONES.map((m, i) => (
          <Typography
            key={m}
            sx={{
              fontSize: 11.5,
              fontFamily: MONO_FONT,
              color:
                i < step
                  ? colors.textSecondary
                  : i === step
                    ? ui.accent
                    : colors.textTertiary,
              textDecoration: i < step ? "line-through" : "none",
              opacity: i > step ? 0.7 : 1,
            }}
          >
            {i < step ? "done" : i === step ? "now" : "next"} · {m}
            {i < MILESTONES.length - 1 ? "  " : ""}
          </Typography>
        ))}
      </Box>

      <Typography
        sx={{ mt: 2, fontSize: 11.5, color: colors.textSecondary }}
      >
        Typically ~{expectedMin} min. Results appear here when the run finishes.
      </Typography>
    </Box>
  );
}
